import { useTheme } from "@mui/material/styles";
import { REFERENCE_DASH } from "../../Theme";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import ChartCard from "./ChartCard";
import ChartTooltip, { TooltipEntry } from "./ChartTooltip";

export interface Series {
  key: string;
  name: string;
  color: string;
  /** Drawn dashed, for a series that is a reference rather than a forecast. */
  dashed?: boolean;
}

/**
 * Every chart on this page: same card, same grid, same tooltip, same legend.
 *
 * Lines over a numeric lead time axis or bars over categories — the only two shapes the page
 * needs. Adding a chart is an entry of configuration here rather than a new file, which is
 * what keeps the count down as the page grows.
 */
const ForecastChart = ({
  title,
  subtitle,
  data,
  series,
  xKey,
  xTicks,
  xDomain,
  formatX,
  formatY,
  formatValue,
  xLabel,
  bars = false,
}: {
  title: string;
  subtitle: string;
  data: object[];
  series: Series[];
  xKey: string;
  /** Numeric axis when given: lead times are sampled unevenly and must not be equidistant. */
  xTicks?: number[];
  xDomain?: [number, number];
  formatX?: (value: number | string) => string;
  formatY: (value: number) => string;
  formatValue: (value: number) => string;
  xLabel?: string;
  bars?: boolean;
}) => {
  const theme = useTheme();
  const axisColor = theme.vars.palette.chart.axis;
  const Chart = bars ? BarChart : LineChart;

  return (
    <ChartCard title={title} subtitle={subtitle}>
      <ResponsiveContainer width="100%" height="100%">
        <Chart
          data={data}
          margin={{ top: 4, right: 16, left: 4, bottom: 16 }}
          barGap={2}
          barCategoryGap="25%"
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={theme.vars.palette.chart.grid}
          />
          <XAxis
            dataKey={xKey}
            type={xDomain ? "number" : "category"}
            domain={xDomain}
            ticks={xTicks}
            tickFormatter={formatX}
            interval={bars ? 0 : undefined}
            angle={bars ? -30 : undefined}
            textAnchor={bars ? "end" : undefined}
            height={bars ? 60 : undefined}
            tick={{ fontSize: bars ? 10 : 11, fill: axisColor }}
            stroke={axisColor}
            label={
              xLabel
                ? {
                    value: xLabel,
                    position: "insideBottom",
                    offset: -8,
                    fill: axisColor,
                    fontSize: 11,
                  }
                : undefined
            }
          />
          <YAxis
            tick={{ fontSize: 11, fill: axisColor }}
            stroke={axisColor}
            tickFormatter={formatY}
          />
          <Tooltip
            cursor={bars ? { fill: "transparent" } : undefined}
            content={(props) => (
              <ChartTooltip
                active={props.active}
                payload={props.payload as TooltipEntry[]}
                label={
                  props.label === undefined
                    ? undefined
                    : formatX
                    ? formatX(props.label as number | string)
                    : String(props.label)
                }
                formatValue={formatValue}
              />
            )}
          />
          <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
          {series.map((entry) =>
            bars ? (
              <Bar
                key={entry.key}
                dataKey={entry.key}
                name={entry.name}
                fill={entry.color}
                radius={[4, 4, 0, 0]}
              />
            ) : (
              <Line
                key={entry.key}
                type="monotone"
                dataKey={entry.key}
                name={entry.name}
                stroke={entry.color}
                strokeWidth={2}
                strokeDasharray={entry.dashed ? REFERENCE_DASH : undefined}
                dot={false}
                activeDot={{ r: 4 }}
              />
            )
          )}
        </Chart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

export default ForecastChart;
